import { Label, TextInput } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import { ResetPasswordMeta } from '../data'
import { Button, Card, SplitBackgroundLayout } from '../components';

// Gets the API base URL from environment variables or defaults to localhost
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

function ResetPassword() {
    const meta = ResetPasswordMeta
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null); // Added for success message

    const [formData, setFormData] = useState({
        password: "",
        password_confirmation: "",
        token: "",
        email: ""
    });

    // 1. Extract Token and Email from URL on component load
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        const email = urlParams.get('email');

        if (token && email) {
            setFormData(prevData => ({
                ...prevData,
                token: token,
                email: email
            }));
        } else {
            // Error if token or email are missing, meaning the link is bad
            setError(meta.invalidLinkError);
        }
    }, []);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null); // Clear previous success/error
        setLoading(true);

        // Basic client-side validation
        if (formData.password !== formData.password_confirmation) {
            setError(meta.passwordsDoNotMatch);
            setLoading(false);
            return;
        }

        if (!formData.token || !formData.email) {
            setError(meta.missingTokenError);
            // setLoading(false);
            return;
        }

        // 4. Include token and email in the reset payload
        const resetData = {
            token: formData.token,
            email: formData.email,
            password: formData.password,
            password_confirmation: formData.password_confirmation
        };

        try {
            const response = await fetch(API_BASE_URL + '/auth/resetpassword',
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                // credentials: 'include', // Only needed if sending cookies/session with request
                body: JSON.stringify(resetData)
            });

            if (!response.ok) {
                // Read the specific error message from the backend response
                const errorData = await response.json();
                // Use a generic message if backend doesn't provide one
                throw new Error(errorData.message || 'Password reset failed. Please check your token/email.');
            }

            // Success
            const data = await response.json();
            setSuccess(data.message || 'Password has been reset successfully. Redirecting to login...');

            // Redirect after a short delay so user can read the success message
            setTimeout(() => {
                window.location.href = "/international/login";
            }, 3000);

        } catch (e) {
            // Catch network errors or errors thrown above
            setError(e.message || 'An unexpected error occurred during reset.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <SplitBackgroundLayout>
            {(error || success) && (
                <div className="w-full max-w-sm mb-4">
                    <div className={`${error ? 'bg-danger-500/10 border-danger-500 text-danger-500' : 'bg-success-500/10 border-success-500 text-success-500'} border px-6 py-4 rounded-lg`}>
                        <p className="font-bold text-lg mb-2">{error ? meta.errorLabel : meta.successLabel}</p>
                        <p>{error || success}</p>
                    </div>
                </div>
            )}

            <Card className="w-full max-w-sm shadow-lg p-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-4">
                    {meta.title}
                </h3>

                <form className="flex flex-col gap-4" onSubmit={handleSubmit}>

                    <div>
                        {/* 2. Hidden fields are now unnecessary as token/email are in state/payload */}
                        <div className="mb-1 block">
                            <Label htmlFor="password">{meta.passwordLabel}</Label>
                        </div>
                        <TextInput
                            id="password"
                            type="password"
                            name="password"
                            placeholder={meta.passwordPlaceholder}
                            value={formData.password}
                            onChange={handleChange}
                            required
                        />
                        <div className="mb-1 block">
                            <Label htmlFor="password_confirmation" > {meta.confirmPasswordLabel}</Label>
                        </div>
                        <TextInput
                            id="password_confirmation"
                            type="password"
                            name="password_confirmation"
                            placeholder={meta.confirmPasswordPlaceholder}
                            value={formData.password_confirmation}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    {/* Submit Button */}
                    <Button type="submit" disabled={loading}>
                        {loading ? meta.resetting : meta.resetButton}
                    </Button>

                </form>
            </Card>
        </SplitBackgroundLayout>
    );
}

export default ResetPassword;
